import {
    chatCompletion,
    ChatStreamProtocolError,
    retryAssistantMessage,
    StrictTurnHandoffParser,
    type IChatAttachmentCreate,
    type ICitation,
    type IChatAttachmentBrief,
    type OpenChatStream,
} from "@/api";
import type {
    ContentBlock,
    IAgenticStreamEvent,
    IContextEnrichmentDataPart,
    ICitationResolution,
} from "@/api/chat/chat.types";
import { queryClient as appQueryClient } from "@/api/queryClient";
import type { IReportVersionSummary } from "@/api/reports/reports.types";
import {
    conversationReportsQueryKey,
    readFreshConversationReports,
} from "@/hooks/useConversationReports";
import type { AttachmentType } from "@/api/attachments";
import { trackChatMessageSent } from "@/lib/analytics";
import { NDJSONParseError, parseNDJSONStream } from "@/utils/events";
import type {
    ChatModelAdapter,
    ChatModelRunResult,
    ChatModelRunUpdate,
} from "@assistant-ui/react";
import type {
    ChainStep,
    ThinkingStep,
    ToolStep,
    ProcessingStep,
} from "../components/ChainOfThought";
import { reportDataPart, type IReportDataPart } from "../utils/content-parts";
import {
    ChatDocumentLimitExceededError,
    extractChatDocumentLimitError,
} from "@/api/chat/chat-document-limit-error";
import { publishDirectDocumentBudgetRejection } from "@/components/shared/direct-document-budget";
import {
    forgetActiveTurn,
    forgetActiveTurnIf,
    rememberActiveTurn,
    resolveActiveTurn,
    type CapturedTurnIdentity,
} from "@/utils/active-turn-identity";

function isAbortError(err: unknown): boolean {
    if (err instanceof DOMException && err.name === "AbortError") return true;
    const cancellation = err as { name?: string; code?: string } | null;
    return (
        cancellation?.name === "CanceledError" ||
        cancellation?.code === "ERR_CANCELED"
    );
}

/** How long a finished turn's last update, or a history import, waits for the report list. */
export const REPORT_PART_WAIT_MS = 5_000;

/**
 * The server id of the user message a run answers, when the thread holds it:
 * a Retry or an attached run answers a message loaded from history.
 */
function answeredUserMessageId(
    messages: readonly { role: string; id: string }[]
): number | undefined {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
        const message = messages[index];
        if (message.role !== "user") continue;
        const id = /^\d+$/.test(message.id) ? Number(message.id) : NaN;
        return Number.isSafeInteger(id) ? id : undefined;
    }
    return undefined;
}

/** The version a completed `generate_report` call admitted, if it admitted one. */
function admittedReportVersionId(
    block: Partial<ContentBlock>
): number | undefined {
    if (
        block.type !== "tool_use" ||
        block.name !== "generate_report" ||
        block.status === "error" ||
        block.result?.success !== true
    ) {
        return undefined;
    }
    const data: unknown = block.result.data;
    if (!data || typeof data !== "object") return undefined;
    const { outcome, version_id: versionId } = data as {
        outcome?: unknown;
        version_id?: unknown;
    };
    return outcome === "accepted" && typeof versionId === "number"
        ? versionId
        : undefined;
}

function hasStreamEventType(event: unknown): event is { type: string } {
    return (
        typeof event === "object" &&
        event !== null &&
        !Array.isArray(event) &&
        typeof (event as { type?: unknown }).type === "string"
    );
}

export interface ChatIdentitySnapshot {
    userId: number | null;
    organizationId: number | null;
    authSessionEpoch: number;
}

/**
 * An absent snapshot on both sides is a match: a caller that supplies no
 * identity has nothing that can be invalidated mid-run.
 */
export function sameChatOrganization(
    captured: ChatIdentitySnapshot | undefined,
    current: ChatIdentitySnapshot | undefined
): boolean {
    return (
        (captured?.organizationId ?? null) === (current?.organizationId ?? null)
    );
}

export function sameChatPrincipalSession(
    captured: ChatIdentitySnapshot | undefined,
    current: ChatIdentitySnapshot | undefined
): boolean {
    return (
        (captured?.userId ?? null) === (current?.userId ?? null) &&
        (captured?.authSessionEpoch ?? null) ===
            (current?.authSessionEpoch ?? null)
    );
}

export interface AttachTarget {
    assistantMessageId: number | string;
    /** Injected for tests; production installs `openAttachStream` from `@/api`. */
    openAttachStream: (
        conversationId: number | string,
        assistantMessageId: number | string,
        signal?: AbortSignal
    ) => Promise<ReadableStream<Uint8Array>>;
    /**
     * Every non-terminal end of an attached run, so its owner can restore the
     * branch. The adapter decides nothing here: no re-attach, no retry, no
     * backoff.
     */
    onAttachEnd?: () => void;
}

export interface ChatModelAdapterDeps {
    getThreadId?: () => string | undefined;
    onComplete?: (threadId: string) => void;
    /**
     * One-shot target for a passive attach, read and cleared on entry to
     * `run()` exactly like the refine and retry markers below. One adapter
     * instance serves every thread and every run, so a statically supplied
     * target would send each composer turn down the attach branch.
     */
    getAttachTarget?: () => AttachTarget | undefined;
    clearAttachTarget?: () => void;
    /**
     * Re-read durable state after a post-admission transport failure or a
     * recoverable server error event. User stops and pre-stream failures do not
     * cross this boundary.
     */
    onMidStreamError?: (threadId: string) => void;
    /**
     * One-shot getter for the `refined_from_message_id` piggyback on the
     * next `/completion` request. `ContextEnrichmentCard` writes the
     * source assistant message id via the RefineContext ref immediately
     * before calling `aui.thread().append(...)`; the adapter reads it on
     * entry to `run()` and clears the ref (via `clearRefinedFromMessageId`)
     * so an unrelated composer-sent user turn doesn't inherit the marker.
     */
    getRefinedFromMessageId?: () => number | undefined;
    clearRefinedFromMessageId?: () => void;
    /**
     * One-shot assistant-message id to replay. When set, `run()` posts to the
     * retry endpoint instead of `/completion` and sends no prompt or attachments,
     * because the server replays the preceding user turn itself. Read and cleared
     * at the top of `run()`, exactly like the refine marker.
     */
    getRetryTargetMessageId?: () => number | undefined;
    clearRetryTargetMessageId?: () => void;
    /**
     * Invoked on any pre-stream exit of the `/completion` request while
     * the refine piggyback was active (network failure, 4xx/5xx, or
     * user-initiated abort). Before the stream opens the atomic backend
     * bundle (user message + source flip + counter reset) has not
     * committed, so the source enrichment's `refined` flag is still
     * false. The card (and the RuntimeProvider-level always-on listener)
     * use this to roll back the session-scoped optimistic "Refined above"
     * mark. Mid-stream errors are not reported: by that point the commit
     * has already persisted and the optimistic state is correct.
     */
    onRefineFailed?: (refinedFromMessageId: number) => void;
    /**
     * Read once at the start of every completion and retry run, before any
     * request, and again at the handoff so a principal, organization, or auth
     * session that moved underneath the run is visible.
     */
    getIdentitySnapshot?: () => ChatIdentitySnapshot;
    /**
     * The turn is committed server-side with nothing left to stream. Fired
     * only after this run has quiesced, and only under the captured scope.
     */
    onSettledAtRest?: (threadId: string) => void;
    /**
     * The server admitted this completion or retry run and opened its stream,
     * so the user turn is committed. Fired once, before any streamed event, and
     * only under the captured scope.
     */
    onTurnAdmitted?: (threadId: string) => void;
}

export function createChatModelAdapter(
    deps: ChatModelAdapterDeps
): ChatModelAdapter {
    return {
        async *run({ messages, abortSignal, unstable_threadId }) {
            const threadId = unstable_threadId || deps.getThreadId?.();
            if (!threadId) return;
            const conversationId = Number(threadId);
            const hasConversationId = Number.isSafeInteger(conversationId);
            const answeredMessageId = answeredUserMessageId(messages);

            const capturedIdentity = deps.getIdentitySnapshot?.();
            // Only a run that knows whose turn it is may claim one: without a
            // snapshot the registry would hold an entry no scope can own.
            const capturedTurn: CapturedTurnIdentity | undefined =
                capturedIdentity && {
                    ...capturedIdentity,
                    conversationId: threadId,
                };

            // Read-and-clear ahead of the send preamble, so an attached viewer
            // assembles no prompt, reports no send, and consumes none of the
            // sender's one-shot markers.
            const attachTarget = deps.getAttachTarget?.();
            deps.clearAttachTarget?.();

            const toolCalls = new Map<
                number,
                {
                    type: "tool-call";
                    toolCallId: string;
                    toolName: string;
                    args: Record<string, unknown>;
                    argsText: string;
                }
            >();
            const sources: unknown[] = [];
            const citations = new Map<number, ICitation>();
            const autoAttachments: IChatAttachmentBrief[] = [];
            // Inline cite:// chip resolutions. Keyed by handle so repeated
            // citation_resolved events dedupe last-wins, mirroring the reload
            // path's merge in convert-messages.
            const citationResolutions = new Map<string, ICitationResolution>();
            // Memoized snapshot of the map's values. Rebuilt only when a
            // citation_resolved event mutates the map (see below), so every
            // other yield reuses the same array reference and the assistant-ui
            // runtime doesn't churn metadata.custom on each block_delta.
            let citationSnapshot: ICitationResolution[] | undefined;

            // Render groups: interleave chain-of-thought with text,
            // matching the AgenticMessage pattern where text blocks
            // break consecutive work block grouping.
            type RenderGroup =
                | { kind: "cot"; steps: ChainStep[] }
                | { kind: "text"; text: string }
                | { kind: "enrichment"; data: IContextEnrichmentDataPart }
                | {
                      kind: "template-render";
                      data: { conversationId: number; messageId: number };
                  };
            const renderGroups: RenderGroup[] = [];

            // The stream never names the user message a report answers, so a
            // list read maps the admitted version to its row; rows, not tool
            // blocks, decide whether a card exists. A run answering a message
            // from history also reads that message's rows at its end, as the
            // reload does.
            const reportParts = new Map<number, IReportDataPart>();
            const reportReads: Promise<void>[] = [];

            function readReportParts(
                answers: (row: IReportVersionSummary) => boolean
            ) {
                reportReads.push(
                    readFreshConversationReports(
                        appQueryClient,
                        conversationId
                    ).then(
                        (rows) => {
                            for (const row of rows) {
                                const chatMessageId = row.chat_message_id;
                                if (
                                    answers(row) &&
                                    typeof chatMessageId === "number"
                                ) {
                                    reportParts.set(chatMessageId, {
                                        conversationId,
                                        chatMessageId,
                                    });
                                }
                            }
                        },
                        () => undefined
                    )
                );
            }

            async function settleReportReads() {
                if (reportReads.length === 0 || abortSignal.aborted) return;
                let timer: ReturnType<typeof setTimeout> | undefined;
                let stop: () => void = () => undefined;
                await Promise.race([
                    Promise.all(reportReads),
                    new Promise<void>((resolve) => {
                        stop = () => resolve();
                        timer = setTimeout(stop, REPORT_PART_WAIT_MS);
                        abortSignal.addEventListener("abort", stop, {
                            once: true,
                        });
                    }),
                ]);
                clearTimeout(timer);
                abortSignal.removeEventListener("abort", stop);
            }

            // Maps stream block indices to step references for mutation
            const blockToStep = new Map<number, ChainStep>();

            // Track the last non-empty CoT group index to avoid scanning
            let lastCotGroupIndex = -1;

            function currentCotGroup() {
                const last = renderGroups[renderGroups.length - 1];
                if (last?.kind === "cot") return last;
                const group = {
                    kind: "cot" as const,
                    steps: [] as ChainStep[],
                };
                renderGroups.push(group);
                lastCotGroupIndex = renderGroups.length - 1;
                return group;
            }

            function currentTextGroup() {
                const last = renderGroups[renderGroups.length - 1];
                if (last?.kind === "text") return last;
                const group = { kind: "text" as const, text: "" };
                renderGroups.push(group);
                return group;
            }

            // Each wire text block owns one group, as reload maps each
            // persisted text block to its own part. Sharing the last group let
            // a later block's completion replace an earlier block's text.
            const blockToTextGroup = new Map<
                number,
                { kind: "text"; text: string }
            >();

            function textGroupFor(index: number | undefined) {
                if (index === undefined) return currentTextGroup();
                let group = blockToTextGroup.get(index);
                if (!group) {
                    group = { kind: "text" as const, text: "" };
                    renderGroups.push(group);
                    blockToTextGroup.set(index, group);
                }
                return group;
            }

            // Settle any block still in flight when the turn ends abnormally
            // (stream error or user abort) so its chain-of-thought row shows a
            // terminal state instead of spinning forever. The error itself is
            // surfaced separately via the message-level status (error banner).
            function markInProgressInterrupted() {
                for (const step of blockToStep.values()) {
                    if (step.status === "in_progress") {
                        step.status = "interrupted";
                    }
                }
            }

            type ContentParts = ChatModelRunUpdate["content"];

            function buildContent(): ContentParts {
                const parts: unknown[] = [];
                let hasCot = false;

                for (let i = 0; i < renderGroups.length; i++) {
                    const group = renderGroups[i];
                    if (group.kind === "cot" && group.steps.length > 0) {
                        hasCot = true;
                        parts.push({
                            type: "data",
                            name: "chain-of-thought",
                            data: {
                                steps: group.steps,
                                isLastGroup: i === lastCotGroupIndex,
                            },
                        });
                    } else if (group.kind === "text" && group.text) {
                        parts.push({ type: "text", text: group.text });
                    } else if (group.kind === "enrichment") {
                        parts.push({
                            type: "data",
                            name: "context-enrichment",
                            data: group.data,
                        });
                    } else if (group.kind === "template-render") {
                        parts.push({
                            type: "data",
                            name: "template-render",
                            data: group.data,
                        });
                    }
                }

                for (const part of reportParts.values()) {
                    parts.push(reportDataPart(part));
                }

                if (!hasCot) {
                    for (const tc of toolCalls.values()) {
                        parts.push(tc);
                    }
                }
                for (const src of sources) {
                    parts.push(src);
                }
                return parts as ContentParts;
            }

            // Spread onto the citation_resolved yield and the terminal yields
            // so resolved chips reach AssistantMessage via
            // message.metadata.custom — the same channel convert-messages uses
            // on reload (thread.tsx reads metadata.custom.citation_resolutions).
            // Returns undefined until a citation_resolved arrives, so turns
            // without inline citations keep their exact prior update shape.
            function citationMetadata() {
                if (citationResolutions.size === 0) return undefined;
                citationSnapshot ??= [...citationResolutions.values()];
                return {
                    metadata: {
                        custom: { citation_resolutions: citationSnapshot },
                    },
                };
            }

            let stream: ReadableStream<Uint8Array>;
            let opened: OpenChatStream | undefined;
            if (attachTarget) {
                try {
                    stream = await attachTarget.openAttachStream(
                        threadId,
                        attachTarget.assistantMessageId,
                        abortSignal
                    );
                } catch (err: unknown) {
                    if (isAbortError(err)) {
                        return;
                    }
                    attachTarget.onAttachEnd?.();
                    throw err;
                }
            } else {
                // Find last user message (backward scan, no array copy)
                let lastUserMsg: (typeof messages)[number] | undefined;
                for (let i = messages.length - 1; i >= 0; i--) {
                    if (messages[i].role === "user") {
                        lastUserMsg = messages[i];
                        break;
                    }
                }

                const prompt = lastUserMsg
                    ? lastUserMsg.content
                          .filter((p) => p.type === "text")
                          .map(
                              (p) => (p as { type: "text"; text: string }).text
                          )
                          .join("")
                    : "";

                const attachments: IChatAttachmentCreate[] = (
                    lastUserMsg?.attachments ?? []
                ).map((a) => ({
                    attachment_id: Number(a.id?.split(":")[1] ?? 0),
                    attachment_type: a.type as AttachmentType,
                    attachment_name: a.name ?? "",
                    attachment_subtitle: "",
                    ...((a as Record<string, unknown>).contentType === "kb" && {
                        is_knowledge_base: true,
                    }),
                }));

                // Read-and-clear the one-shot refined_from_message_id BEFORE
                // issuing the network request. Clearing up-front (not after)
                // keeps the ref clean even if chatCompletion throws, so a
                // subsequent composer-sent user turn doesn't accidentally
                // inherit the marker from a failed refine click.
                const refinedFromMessageId = deps.getRefinedFromMessageId?.();
                deps.clearRefinedFromMessageId?.();

                // Same read-and-clear discipline for the retry target: a failed retry
                // must not leave the next composer-sent turn routed to the retry
                // endpoint.
                const retryTargetMessageId = deps.getRetryTargetMessageId?.();
                deps.clearRetryTargetMessageId?.();

                if (Number.isFinite(conversationId)) {
                    trackChatMessageSent({
                        conversation_id: conversationId,
                        attachment_count: attachments.length,
                        is_refinement: refinedFromMessageId !== undefined,
                    });
                }

                // A previous turn's id must not survive into this request: it
                // names the message a Stop would abandon. Only an entry this
                // same session owns is cleared -- a holder under another auth
                // session must stay visible to Stop as "stale", never "none".
                if (
                    capturedTurn &&
                    resolveActiveTurn(capturedTurn).kind === "match"
                ) {
                    forgetActiveTurn(capturedTurn);
                }

                try {
                    opened =
                        retryTargetMessageId !== undefined
                            ? // Retry carries no prompt and no attachments: the server
                              // replays the preceding user turn itself, drops the
                              // targeted assistant message and everything after it,
                              // then streams on the same event contract.
                              await retryAssistantMessage(
                                  threadId,
                                  retryTargetMessageId,
                                  abortSignal
                              )
                            : await chatCompletion(
                                  threadId,
                                  {
                                      prompt,
                                      attachments,
                                      ...(refinedFromMessageId !==
                                          undefined && {
                                          refined_from_message_id:
                                              refinedFromMessageId,
                                      }),
                                  },
                                  abortSignal
                              );
                    stream = opened.stream;
                    if (capturedTurn) {
                        rememberActiveTurn(
                            capturedTurn,
                            opened.assistantMessageId
                        );
                    }
                } catch (err: unknown) {
                    // A response-protocol error is known to happen after the
                    // server answered and may have committed the refinement.
                    // Other pre-stream exits still roll back the optimistic
                    // marker because no admitted response was observed.
                    if (
                        refinedFromMessageId !== undefined &&
                        !(err instanceof ChatStreamProtocolError)
                    ) {
                        deps.onRefineFailed?.(refinedFromMessageId);
                    }
                    if (isAbortError(err)) {
                        return;
                    }
                    // A document-limit rejection arrives as a `detail` OBJECT, which
                    // `getApiErrorMessage` degrades to its generic fallback. Convert
                    // it here so the limit survives the throw, and tell the composer
                    // its selection was refused rather than lost.
                    const limitError = await extractChatDocumentLimitError(err);
                    if (limitError) {
                        publishDirectDocumentBudgetRejection({
                            limit: limitError.limit,
                        });
                        throw new ChatDocumentLimitExceededError(
                            limitError.limit
                        );
                    }
                    throw err;
                }

                const admittedUnder = deps.getIdentitySnapshot?.();
                if (
                    sameChatOrganization(capturedIdentity, admittedUnder) &&
                    sameChatPrincipalSession(capturedIdentity, admittedUnder)
                ) {
                    deps.onTurnAdmitted?.(threadId);
                }
            }

            const handoffParser =
                opened &&
                new StrictTurnHandoffParser(opened.assistantMessageId);

            function protocolFailure(): ChatModelRunResult {
                markInProgressInterrupted();
                return {
                    content: buildContent(),
                    ...citationMetadata(),
                    status: {
                        type: "incomplete" as const,
                        reason: "error" as const,
                        error: "An error occurred. Please try again.",
                    },
                };
            }

            try {
                for await (const event of parseNDJSONStream<IAgenticStreamEvent>(
                    stream,
                    { strict: true }
                )) {
                    if (!hasStreamEventType(event)) {
                        throw new ChatStreamProtocolError(
                            "The stream event envelope is invalid"
                        );
                    }
                    switch (event.type) {
                        case "block_start": {
                            const block = event.block;
                            if (!block) break;

                            if (
                                block.type === "tool_use" &&
                                event.index !== undefined
                            ) {
                                toolCalls.set(event.index, {
                                    type: "tool-call",
                                    toolCallId:
                                        block.id ?? `tool_${event.index}`,
                                    toolName: block.name ?? "",
                                    args: block.input ?? {},
                                    argsText: JSON.stringify(block.input ?? {}),
                                });
                                const step: ToolStep = {
                                    type: "tool",
                                    toolName: block.name ?? "",
                                    toolCallId:
                                        block.id ?? `tool_${event.index}`,
                                    status: "in_progress",
                                    args: block.input,
                                    loadingText: block.loadingText,
                                };
                                blockToStep.set(event.index, step);
                                currentCotGroup().steps.push(step);
                            } else if (block.type === "processing") {
                                const step: ProcessingStep = {
                                    type: "processing",
                                    name: block.name ?? "",
                                    status: block.status ?? "in_progress",
                                    summaryText: block.summary_text,
                                };
                                if (event.index !== undefined) {
                                    blockToStep.set(event.index, step);
                                }
                                currentCotGroup().steps.push(step);
                            } else if (block.type === "thinking") {
                                const step: ThinkingStep = {
                                    type: "thinking",
                                    text: "",
                                    status: "in_progress",
                                };
                                if (event.index !== undefined) {
                                    blockToStep.set(event.index, step);
                                }
                                currentCotGroup().steps.push(step);
                            } else if (block.type === "context_enrichment") {
                                // Live-stream enrichment: always the latest
                                // card when it arrives. Earlier cards in
                                // history are re-annotated on rehydration.
                                // Drop the block silently if chips or the
                                // free-text prompt are missing — rendering
                                // an empty chip row is worse than omitting
                                // the card until the server emits a valid
                                // payload.
                                const chips = block.chips;
                                const promptText = block.free_text_prompt;
                                if (
                                    chips &&
                                    chips.length > 0 &&
                                    typeof promptText === "string"
                                ) {
                                    // threadId is the conversation id on
                                    // this runtime; messageId now rides on
                                    // the block envelope (service.py emits
                                    // `message_id` alongside the block so
                                    // the card can reach the dismiss /
                                    // refine endpoints immediately instead
                                    // of waiting for a page refresh).
                                    const messageIdNum =
                                        typeof event.message_id === "number"
                                            ? event.message_id
                                            : undefined;
                                    renderGroups.push({
                                        kind: "enrichment",
                                        data: {
                                            chips,
                                            freeTextPrompt: promptText,
                                            dismissed: block.dismissed ?? false,
                                            refined: block.refined ?? false,
                                            isLatestActionable: true,
                                            conversationId: Number.isFinite(
                                                conversationId
                                            )
                                                ? conversationId
                                                : undefined,
                                            messageId: messageIdNum,
                                        },
                                    });
                                }
                            } else if (block.type === "template_render") {
                                const c = block.conversationId;
                                const m = block.messageId;
                                if (
                                    typeof c === "number" &&
                                    typeof m === "number"
                                ) {
                                    renderGroups.push({
                                        kind: "template-render",
                                        data: {
                                            conversationId: c,
                                            messageId: m,
                                        },
                                    });
                                }
                            }

                            yield {
                                content: buildContent(),
                                ...citationMetadata(),
                            };
                            break;
                        }

                        case "block_delta": {
                            if (event.text !== undefined) {
                                textGroupFor(event.index).text += event.text;
                            }
                            if (
                                event.thinking !== undefined &&
                                event.index !== undefined
                            ) {
                                const step = blockToStep.get(event.index);
                                if (step?.type === "thinking") {
                                    step.text += event.thinking;
                                }
                            }
                            if (
                                event.summary_text !== undefined &&
                                event.index !== undefined
                            ) {
                                const step = blockToStep.get(event.index);
                                if (step?.type === "processing") {
                                    step.summaryText = event.summary_text;
                                }
                            }
                            yield {
                                content: buildContent(),
                                ...citationMetadata(),
                            };
                            break;
                        }

                        case "block_complete": {
                            const block = event.block;
                            if (!block) break;

                            if (
                                block.type === "text" &&
                                block.text !== undefined
                            ) {
                                // A block can complete without ever sending a
                                // delta: the fallbacks and the incomplete-answer
                                // notice carry their text on the block itself,
                                // and the notice follows the answer it qualifies.
                                textGroupFor(event.index).text = block.text;
                            }
                            if (
                                block.type === "tool_use" &&
                                event.index !== undefined
                            ) {
                                toolCalls.set(event.index, {
                                    type: "tool-call",
                                    toolCallId:
                                        block.id ?? `tool_${event.index}`,
                                    toolName: block.name ?? "",
                                    args: block.input ?? {},
                                    argsText: JSON.stringify(block.input ?? {}),
                                });
                            }
                            const reportVersionId =
                                admittedReportVersionId(block);
                            if (
                                reportVersionId !== undefined &&
                                hasConversationId
                            ) {
                                readReportParts(
                                    (row) => row.id === reportVersionId
                                );
                            }

                            if (event.index !== undefined) {
                                const step = blockToStep.get(event.index);
                                if (step) {
                                    const blockStatus = block.status;
                                    step.status =
                                        blockStatus === "error"
                                            ? "error"
                                            : "complete";

                                    if (
                                        block.type === "thinking" &&
                                        step.type === "thinking"
                                    ) {
                                        if (block.thinking !== undefined) {
                                            step.text = block.thinking;
                                        }
                                        step.durationMs = block.durationMs;
                                    }
                                    if (
                                        block.type === "tool_use" &&
                                        step.type === "tool"
                                    ) {
                                        step.durationMs = block.durationMs;
                                        step.args = block.input;
                                        step.loadingText = block.loadingText;
                                        step.resultSummary =
                                            block.resultSummary;
                                        step.result = block.result;
                                    }
                                    if (
                                        block.type === "processing" &&
                                        step.type === "processing"
                                    ) {
                                        step.summaryText = block.summary_text;
                                        step.durationMs = block.durationMs;
                                        if (blockStatus) {
                                            step.status =
                                                blockStatus === "error"
                                                    ? "error"
                                                    : "complete";
                                        }
                                    }
                                }
                            }

                            yield {
                                content: buildContent(),
                                ...citationMetadata(),
                            };
                            break;
                        }

                        case "citations": {
                            if (event.citations) {
                                for (const c of event.citations) {
                                    citations.set(c.document_id, c);
                                    if (!c.document_url) continue;
                                    const source = {
                                        type: "source",
                                        sourceType: "url",
                                        id: String(c.document_id),
                                        url: c.document_url,
                                        title: c.document_name,
                                    };
                                    const sourceIndex = sources.findIndex(
                                        (item) =>
                                            typeof item === "object" &&
                                            item !== null &&
                                            (item as { type?: unknown })
                                                .type === "source" &&
                                            (item as { id?: unknown }).id ===
                                                source.id
                                    );
                                    if (sourceIndex === -1) {
                                        sources.push(source);
                                    } else {
                                        sources[sourceIndex] = source;
                                    }
                                }
                            }
                            yield {
                                content: buildContent(),
                                ...citationMetadata(),
                            };
                            break;
                        }

                        case "citation_resolved": {
                            if (event.resolution_map) {
                                for (const r of event.resolution_map) {
                                    citationResolutions.set(r.handle, r);
                                }
                                // Invalidate the memoized snapshot so the next
                                // read rebuilds it once with the new resolutions.
                                citationSnapshot = undefined;
                            }
                            yield {
                                content: buildContent(),
                                ...citationMetadata(),
                            };
                            break;
                        }

                        case "attachments": {
                            if (event.attachments) {
                                autoAttachments.push(...event.attachments);
                            }
                            break;
                        }

                        case "error": {
                            markInProgressInterrupted();
                            if (event.recoverable) {
                                // An attached run reports to its own owner. The
                                // legacy channel drives MidStreamRecoveryReloader,
                                // which imports canonical history behind nothing
                                // but a thread-id check -- a second, uncoordinated
                                // recovery on top of a re-armed attachment.
                                if (attachTarget) {
                                    attachTarget.onAttachEnd?.();
                                } else if (threadId) {
                                    deps.onMidStreamError?.(threadId);
                                }
                            }
                            yield {
                                content: buildContent(),
                                ...citationMetadata(),
                                status: {
                                    type: "incomplete" as const,
                                    reason: "error" as const,
                                    error:
                                        event.message ||
                                        "An error occurred. Please try again.",
                                },
                            };
                            return;
                        }

                        case "message_complete": {
                            if (
                                hasConversationId &&
                                answeredMessageId !== undefined
                            ) {
                                readReportParts(
                                    (row) =>
                                        row.chat_message_id ===
                                        answeredMessageId
                                );
                            }
                            await settleReportReads();
                            // Add references before final build so the
                            // data part is included in the content array.
                            sources.push({
                                type: "data",
                                name: "references",
                                data: {
                                    citations: [...citations.values()],
                                    autoAttachments,
                                    conversationAttachments: [],
                                },
                            });

                            yield {
                                content: buildContent(),
                                ...citationMetadata(),
                                status: {
                                    type: "complete" as const,
                                    reason: "stop" as const,
                                },
                            };
                            // The viewer's own convergence already runs:
                            // RecoveringMessageSlot invalidates and re-imports
                            // canonical history off recovery-status. Firing this
                            // here would also offer a "your answer is ready"
                            // toast for a question the viewer never asked. The
                            // same restraint applies when the principal, org,
                            // or auth session moved underneath the run.
                            const completedUnder = deps.getIdentitySnapshot?.();
                            if (
                                !attachTarget &&
                                sameChatOrganization(
                                    capturedIdentity,
                                    completedUnder
                                ) &&
                                sameChatPrincipalSession(
                                    capturedIdentity,
                                    completedUnder
                                )
                            ) {
                                deps.onComplete?.(threadId);
                            }
                            return;
                        }

                        case "turn_handoff": {
                            // An attached viewer has no header id to agree
                            // with, so it can only treat this as a broken
                            // protocol.
                            if (!handoffParser) {
                                throw new ChatStreamProtocolError(
                                    "An attached stream cannot hand off a turn"
                                );
                            }
                            // Throws on a duplicate envelope, an unknown
                            // disposition, or header/body disagreement.
                            const handoff = handoffParser.parse(event);

                            // Conditional on this run's own id: a delayed
                            // handoff must not delete the entry a newer run
                            // already stored under the same scope.
                            if (capturedTurn && handoff) {
                                forgetActiveTurnIf(
                                    capturedTurn,
                                    handoff.assistant_message_id
                                );
                            }

                            const current = deps.getIdentitySnapshot?.();
                            if (
                                !sameChatOrganization(capturedIdentity, current)
                            ) {
                                markInProgressInterrupted();
                                yield {
                                    content: buildContent(),
                                    ...citationMetadata(),
                                    status: {
                                        type: "incomplete" as const,
                                        reason: "other" as const,
                                    },
                                };
                                return;
                            }
                            if (
                                !sameChatPrincipalSession(
                                    capturedIdentity,
                                    current
                                )
                            ) {
                                return;
                            }

                            yield {
                                content: buildContent(),
                                ...citationMetadata(),
                                status: {
                                    type: "complete" as const,
                                    reason: "stop" as const,
                                },
                            };
                            // After the terminal update, never before: the
                            // committed copy replaces a run that has stopped.
                            deps.onSettledAtRest?.(threadId);
                            return;
                        }

                        case "message_start":
                        default:
                            break;
                    }
                }

                // Reaching here means the stream ended without a terminal
                // record. A clean return would leave the message `running`,
                // which the runtime converts to `complete/unknown` -- a
                // truncated answer presented as a finished one. The reason must
                // stay off "error": MessagePrimitive.Error renders for that one
                // alone, and a reconnect is not a failure to report.
                if (attachTarget) {
                    markInProgressInterrupted();
                    yield {
                        content: buildContent(),
                        ...citationMetadata(),
                        status: {
                            type: "incomplete" as const,
                            reason: "other" as const,
                        },
                    };
                    attachTarget.onAttachEnd?.();
                } else {
                    markInProgressInterrupted();
                    yield {
                        content: buildContent(),
                        ...citationMetadata(),
                        status: {
                            type: "incomplete" as const,
                            reason: "other" as const,
                        },
                    };
                    deps.onMidStreamError?.(threadId);
                }
            } catch (err: unknown) {
                if (isAbortError(err)) {
                    markInProgressInterrupted();
                    yield { content: buildContent(), ...citationMetadata() };
                    return;
                }
                // A broken protocol is not a durable-state question, so it
                // reports visibly instead of asking for a canonical re-read.
                if (
                    err instanceof ChatStreamProtocolError ||
                    err instanceof NDJSONParseError
                ) {
                    void stream.cancel(err).catch(() => undefined);
                    attachTarget?.onAttachEnd?.();
                    yield protocolFailure();
                    return;
                }
                // The stream died after the turn was committed server-side, so
                // the server is still finishing it. Signal that history must be
                // re-read; without it this tab keeps a dead error bubble for an
                // answer that is still in flight.
                if (attachTarget) {
                    attachTarget.onAttachEnd?.();
                } else if (threadId) {
                    deps.onMidStreamError?.(threadId);
                }
                throw err;
            } finally {
                if (hasConversationId) {
                    void appQueryClient.invalidateQueries({
                        queryKey: conversationReportsQueryKey(conversationId),
                    });
                }
                if (capturedTurn && opened) {
                    forgetActiveTurnIf(capturedTurn, opened.assistantMessageId);
                }
            }
        },
    };
}
